import { useCallback, useState } from 'react';
import { ChevronLeft, List } from 'lucide-react';
import { Button } from '@predoc/cow';
import { cn } from '@/lib/utils';
import { FacesheetOutlineSidebar } from '../FacesheetOutlineSidebar';
import { FacesheetSummaryView } from '../summary/FacesheetSummaryView';
import { useFacesheetOutlineNavigation } from '../useFacesheetOutlineNavigation';
import {
  buildFacesheetOutlineItems,
  getOutlineActiveAnchorId,
} from '../viewerState';
import type {
  CriteriaSection,
  FacesheetCriteriaRow,
  FacesheetHtmlSummaryData,
} from '../types';

const OUTLINE_TOGGLE_CLASS =
  'absolute -left-3.5 top-5 z-10 size-7 rounded-full border-neutral-300 bg-white shadow-sm';

interface FacesheetViewerContentProps {
  summary: FacesheetHtmlSummaryData;
  onOpenReference: (
    row: FacesheetCriteriaRow,
    section: CriteriaSection,
    referenceId?: string,
  ) => void;
}

export const FacesheetViewerContent = ({
  summary,
  onOpenReference,
}: FacesheetViewerContentProps) => {
  const [isOutlineCollapsed, setIsOutlineCollapsed] = useState(false);
  const outlineItems = buildFacesheetOutlineItems(summary);
  const defaultOutlineAnchorId = summary.criteriaSections[0]?.anchorId;
  const shouldObserveOutlineAnchor = useCallback(
    (anchorId: string) =>
      anchorId.startsWith('section-') && anchorId !== 'section-demographics',
    [],
  );
  const { scrollContainerRef, getAnchorRef, activeAnchorId, scrollToAnchor } =
    useFacesheetOutlineNavigation({
      defaultActiveAnchorId: defaultOutlineAnchorId,
      shouldObserveAnchor: shouldObserveOutlineAnchor,
    });
  const outlineActiveAnchorId = getOutlineActiveAnchorId(
    summary,
    activeAnchorId,
  );

  const handleToggleOutline = () => {
    setIsOutlineCollapsed((collapsed) => !collapsed);
  };

  return (
    <div className="relative flex min-h-0 flex-1 overflow-hidden">
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto">
        <FacesheetSummaryView
          summary={summary}
          getAnchorRef={getAnchorRef}
          onOpenReferences={onOpenReference}
        />
      </div>

      <div
        className={cn(
          'relative shrink-0 border-l border-neutral-300 bg-neutral-100 transition-[width] duration-200',
          isOutlineCollapsed ? 'w-12' : 'w-[355px]',
        )}
      >
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleToggleOutline}
          aria-label={
            isOutlineCollapsed
              ? 'Expand facesheet outline'
              : 'Collapse facesheet outline'
          }
          aria-expanded={!isOutlineCollapsed}
          className={OUTLINE_TOGGLE_CLASS}
        >
          {isOutlineCollapsed ? (
            <List className="size-4" />
          ) : (
            <ChevronLeft className="size-4" />
          )}
        </Button>

        {!isOutlineCollapsed ? (
          <FacesheetOutlineSidebar
            items={outlineItems}
            activeAnchorId={outlineActiveAnchorId}
            onSelectItem={scrollToAnchor}
          />
        ) : null}
      </div>
    </div>
  );
};
